import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Trash2, Video, Upload, Play, Pencil } from 'lucide-react';
import { showSuccess, showError } from '@/lib/toastHelpers';
import { getAllUserGuideMedia, uploadUserGuideMedia, updateUserGuideMedia, deleteUserGuideMedia, replaceUserGuideMediaFile, deleteUserGuideMediaFile, UserGuideMedia } from '@/lib/user-guide-media-api';
import { FileDropZone } from '@/components/shared/FileDropZone';
import { MediaUploadForm } from '@/components/user-guide/MediaUploadForm';
import { VideoPlayer } from '@/components/shared/VideoPlayer';

const sections = [
  { value: 'all', label: 'All Sections' },
  { value: 'profile', label: 'Profile' },
  { value: 'user-settings', label: 'User Settings' },
  { value: 'dashboard', label: 'Dashboard' },
  { value: 'my-recipes', label: 'My Recipes' },
  { value: 'planner', label: 'Planner' },
  { value: 'profit-goal', label: 'Profit Goal' },
  { value: 'menus', label: 'Menus' },
  { value: 'pantry', label: 'Pantry' },
];

export default function UserGuideMediaManagement() {
  const [selectedSection, setSelectedSection] = useState('all');
  const [showUpload, setShowUpload] = useState(false);
  const [playingMedia, setPlayingMedia] = useState<UserGuideMedia | null>(null);
  const [editingMedia, setEditingMedia] = useState<UserGuideMedia | null>(null);
  const [editForm, setEditForm] = useState({ title: '', description: '', order: 0 });
  const [replacementFile, setReplacementFile] = useState<File | null>(null);

  const { data: allMedia = [], isLoading } = useQuery({
    queryKey: ['user-guide-media'],
    queryFn: getAllUserGuideMedia
  });

  const qc = useQueryClient();
  const invalidateMedia = () => qc.invalidateQueries({ queryKey: ['user-guide-media'] });

  const uploadMutation = useMutation({
    mutationFn: uploadUserGuideMedia,
    onSuccess: () => { invalidateMedia(); showSuccess('Media uploaded successfully'); setShowUpload(false); },
    onError: (e: any) => showError(e?.message || 'Upload failed')
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) => updateUserGuideMedia(id, data),
    onSuccess: () => { invalidateMedia(); showSuccess('Media updated successfully'); setEditingMedia(null); },
    onError: (e: any) => showError(e?.message || 'Update failed')
  });

  const deleteMutation = useMutation({
    mutationFn: deleteUserGuideMedia,
    onSuccess: () => { invalidateMedia(); showSuccess('Media deleted successfully'); },
    onError: (e: any) => showError(e?.message || 'Delete failed')
  });

  const replaceMutation = useMutation({
    mutationFn: ({ id, file }: { id: string; file: File }) => replaceUserGuideMediaFile(id, file),
    onSuccess: () => { invalidateMedia(); showSuccess('File replaced successfully'); setReplacementFile(null); },
    onError: (e: any) => showError(e?.message || 'File replacement failed')
  });

  const deleteFileMutation = useMutation({
    mutationFn: deleteUserGuideMediaFile,
    onSuccess: () => { invalidateMedia(); showSuccess('File deleted from storage'); },
    onError: (e: any) => showError(e?.message || 'File deletion failed')
  });

  const openEdit = (media: UserGuideMedia) => {
    setEditingMedia(media);
    setEditForm({ title: media.title, description: media.description || '', order: media.order });
    setReplacementFile(null);
  };

  const filtered = selectedSection === 'all' ? allMedia : allMedia.filter((m: UserGuideMedia) => m.section === selectedSection);

  return (
    <div className="p-4 sm:p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-xl sm:text-2xl font-bold">User Guide Media</h1>
        <div className="flex items-center gap-2">
          <Select value={selectedSection} onValueChange={setSelectedSection}>
            <SelectTrigger className="w-40"><SelectValue /></SelectTrigger>
            <SelectContent>
              {sections.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button onClick={() => setShowUpload(true)}>
            <Upload className="h-4 w-4 mr-2" /> Upload Media
          </Button>
        </div>
      </div>

      {/* Upload Dialog */}
      <Dialog open={showUpload} onOpenChange={setShowUpload}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Upload New Media</DialogTitle></DialogHeader>
          <MediaUploadForm onUpload={(fd) => uploadMutation.mutate(fd)} isLoading={uploadMutation.isPending} />
        </DialogContent>
      </Dialog>

      {/* Media Player Modal */}
      <Dialog open={!!playingMedia} onOpenChange={() => setPlayingMedia(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>{playingMedia?.title}</DialogTitle></DialogHeader>
          {playingMedia?.mediaType === 'video'
            ? <VideoPlayer src={playingMedia.url} className="w-full rounded-lg" />
            : playingMedia && <img src={playingMedia.url} alt={playingMedia.title} className="w-full rounded-lg" />
          }
          {playingMedia?.description && <p className="text-sm text-muted-foreground">{playingMedia.description}</p>}
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={!!editingMedia} onOpenChange={() => { setEditingMedia(null); setReplacementFile(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Edit Media</DialogTitle></DialogHeader>
          <div className="space-y-4">
            {/* Current media preview */}
            {editingMedia && (
              <div className="relative rounded-lg overflow-hidden bg-muted">
                {editingMedia.mediaType === 'image'
                  ? <img src={editingMedia.url} alt={editingMedia.title} className="w-full max-h-48 object-contain" />
                  : <VideoPlayer src={editingMedia.url} className="w-full max-h-48" />
                }
                <Button size="sm" variant="destructive" className="absolute top-2 right-2"
                  onClick={() => { deleteFileMutation.mutate(editingMedia._id); setEditingMedia(null); }}>
                  <Trash2 className="h-3 w-3 mr-1" /> Delete
                </Button>
              </div>
            )}
            <div><Label>Title</Label>
              <Input value={editForm.title} onChange={e => setEditForm(f => ({ ...f, title: e.target.value }))} />
            </div>
            <div><Label>Description</Label>
              <Textarea value={editForm.description} onChange={e => setEditForm(f => ({ ...f, description: e.target.value }))} />
            </div>
            <div><Label>Display Order</Label>
              <Input type="number" value={editForm.order} onChange={e => setEditForm(f => ({ ...f, order: parseInt(e.target.value) || 0 }))} />
            </div>
            <div>
              <Label>Replace File (optional)</Label>
              <FileDropZone file={replacementFile} onChange={setReplacementFile} />
              {replacementFile && (
                <Button size="sm" variant="outline" className="mt-2 w-full" disabled={replaceMutation.isPending}
                  onClick={() => replaceMutation.mutate({ id: editingMedia!._id, file: replacementFile })}>
                  {replaceMutation.isPending ? 'Uploading...' : 'Upload New File'}
                </Button>
              )}
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => { setEditingMedia(null); setReplacementFile(null); }}>Cancel</Button>
              <Button disabled={updateMutation.isPending}
                onClick={() => updateMutation.mutate({ id: editingMedia!._id, data: editForm })}>
                Save
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Media List */}
      {isLoading ? (
        <p className="text-muted-foreground">Loading...</p>
      ) : filtered.length === 0 ? (
        <p className="text-muted-foreground">No media uploaded yet.</p>
      ) : (
        <div className="flex flex-col gap-2">
          {filtered.map((media: UserGuideMedia) => (
            <div key={media._id} className="flex items-center gap-3 p-3 rounded-lg border bg-card hover:bg-muted/50 transition-colors">
              {/* Thumbnail */}
              <div className="relative w-14 h-14 rounded-md overflow-hidden shrink-0 group cursor-pointer"
                onClick={() => setPlayingMedia(media)}>
                {media.mediaType === 'image' ? (
                  <img src={media.url} alt={media.title} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full bg-muted flex items-center justify-center">
                    <Video className="h-6 w-6 text-muted-foreground" />
                  </div>
                )}
                <div className="absolute inset-0 bg-black/30 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                  <Play className="h-4 w-4 text-white" />
                </div>
              </div>

              {/* Info */}
              <div className="flex-1 min-w-0 cursor-pointer" onClick={() => setPlayingMedia(media)}>
                <p className="font-medium text-sm truncate">{media.title}</p>
                <div className="flex gap-1 mt-1 flex-wrap">
                  <Badge variant="outline" className="text-xs capitalize">{media.section.replace('-', ' ')}</Badge>
                  <Badge variant="secondary" className="text-xs capitalize">{media.mediaType}</Badge>
                </div>
              </div>

              {/* Actions */}
              <div className="flex items-center gap-1 shrink-0">
                <Switch checked={media.isActive}
                  onCheckedChange={(v) => updateMutation.mutate({ id: media._id, data: { isActive: v } })} />
                <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEdit(media)}>
                  <Pencil className="h-4 w-4" />
                </Button>
                <Button variant="ghost" size="icon" className="text-destructive hover:text-destructive h-8 w-8"
                  onClick={() => deleteMutation.mutate(media._id)}>
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
